import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import './styles.scss';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import Parser from 'html-react-parser';
import { useLocation } from 'react-router-dom';
import l from '../../../lang';
import { getIntegrationKeys } from '../../../store/payment-integration/actions';
import MessageError from '../../common/message-error';
import { realoadPage } from '../../../utils/index';
import AccordionLeft from '../../common/accordion-left';
import TopDashboardSection from '../../common/top-dashboard-section';
import AddToShopify from './add-to-shopify';
import AddToMagento from './add-to-magento';
import AddToWooCommerce from './add-to-woocommerce';
import AddForAPI from './add-for-api';
import Input from '../../common/input';
import ButtonCustom from '../../common/__deprecated__/button-custom';
import {
  segmentIntegrationsKeysCopyApiKey,
  segmentIntegrationsKeysCopySecretKey,
  segmentIntegrationsMenuApiIntegration,
  segmentIntegrationsMenuKeys,
  segmentIntegrationsMenuMagento,
  segmentIntegrationsMenuPayButton,
  segmentIntegrationsMenuShopify,
  segmentIntegrationsMenuWoocommerce,
} from '../../../utils/analytics';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const PaymentIntegration = ({ loading, error, errorInfo, apiKey, secretKey, fnGetKeys }) => {
  const queryParams = useQuery();

  const getItem = () => {
    const item = parseInt(queryParams.get('item'), 10);
    if (item && [1, 2, 3, 4].includes(item)) {
      return item;
    }
    return 0;
  };

  const [idxAcordion, setIdxAcordion] = useState(getItem());

  useEffect(() => {
    if (!apiKey && !secretKey) {
      fnGetKeys();
    }
  }, [apiKey, secretKey, fnGetKeys]);

  return (
    <>
      <TopDashboardSection title={l('menu.item.PAYMENT_INTEGRATION')} subtitle={l('menu.item.PAYMENT_INTEGRATION.desc')} />
      <div className="panel payment-integration">
        <div className="payment-integration__container">
          <div className="payment-integration__desc">{Parser(l('paymentIntegration.desc'))}</div>
          {error && (
            <div className="payment-integration__error">
              <MessageError errorInfo={errorInfo} iconClose={<RefreshIcon />} onClose={() => realoadPage()} />
            </div>
          )}
          {!loading && !error && (
            <AccordionLeft
              open={idxAcordion === 1}
              title={l('paymentIntegration.subtitle')}
              onClick={() => {
                segmentIntegrationsMenuKeys();
                setIdxAcordion(idxAcordion === 1 ? 0 : 1);
              }}
              body={
                <div className="payment-integration__content">
                  <div>
                    <div className="payment-integration__keys-desc">
                      {Parser(l('paymentIntegration.keysDesc'))}
                      <ul>
                        <li>{Parser(l('paymentIntegration.keysDesc.1'))}</li>
                        <li>{Parser(l('paymentIntegration.keysDesc.2'))}</li>
                      </ul>
                    </div>
                    <div className="payment-integration__key">
                      <div className="payment-integration__input">
                        <Input
                          label={l('paymentIntegration.apiKeyLabel')}
                          type="password"
                          name="copyKey"
                          value={apiKey}
                          readOnly
                          disabled
                        />
                      </div>
                      <div className="payment-integration__button">
                        <CopyToClipboard text={apiKey} onCopy={() => segmentIntegrationsKeysCopyApiKey()}>
                          <ButtonCustom
                            variant="contained"
                            size="square"
                            name="button-copy"
                            disabled={apiKey === ''}
                            text={l('paymentIntegration.copyApiKey')}
                          />
                        </CopyToClipboard>
                      </div>
                    </div>
                    <div className="payment-integration__key">
                      <div className="payment-integration__input">
                        <Input
                          label={l('paymentIntegration.secretKeyLabel')}
                          type="password"
                          name="copyKey"
                          value={secretKey}
                          readOnly
                          disabled
                        />
                      </div>
                      <div className="payment-integration__button">
                        <CopyToClipboard text={secretKey} onCopy={() => segmentIntegrationsKeysCopySecretKey()}>
                          <ButtonCustom
                            variant="contained"
                            size="square"
                            name="button-copy"
                            disabled={secretKey === ''}
                            text={l('paymentIntegration.copySecretKey')}
                          />
                        </CopyToClipboard>
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
          )}
          <AccordionLeft
            open={idxAcordion === 3}
            title={Parser(l('paymentIntegration.addToShopify'))}
            onClick={() => {
              segmentIntegrationsMenuShopify();
              setIdxAcordion(idxAcordion === 3 ? 0 : 3);
            }}
            body={<AddToShopify />}
          />
          <AccordionLeft
            open={idxAcordion === 4}
            title={Parser(l('paymentIntegration.addToMagento'))}
            onClick={() => {
              segmentIntegrationsMenuMagento();
              setIdxAcordion(idxAcordion === 4 ? 0 : 4);
            }}
            body={<AddToMagento />}
          />
          <AccordionLeft
            open={idxAcordion === 5}
            title={Parser(l('paymentIntegration.apiIntegration'))}
            onClick={() => {
              segmentIntegrationsMenuApiIntegration();
              setIdxAcordion(idxAcordion === 5 ? 0 : 5);
            }}
            body={<AddForAPI />}
          />
          <AccordionLeft
            open={idxAcordion === 6}
            title={Parser(l('paymentIntegration.addToWooCommerce'))}
            onClick={() => {
              segmentIntegrationsMenuWoocommerce();
              setIdxAcordion(idxAcordion === 6 ? 0 : 6);
            }}
            body={<AddToWooCommerce />}
          />
        </div>
      </div>
    </>
  );
};

PaymentIntegration.propTupes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  errorInfo: PropTypes.object,
  apiKey: PropTypes.string,
  secretKey: PropTypes.string,
  fnGetKeys: PropTypes.func,
};

const mapStateToProps = state => ({
  loading: state.paymentIntegration.loading,
  error: state.paymentIntegration.error,
  errorInfo: state.paymentIntegration.errorInfo,
  apiKey: state.paymentIntegration.apiKey,
  secretKey: state.paymentIntegration.secretKey,
});

const mapDispatchToProps = dispatch => ({
  fnGetKeys: () => dispatch(getIntegrationKeys()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentIntegration);
